// Showing money in A$ or US$.
//
// Providers bill in US dollars, so every figure starts in US$. `money` is the
// viewer's choice plus today's rate: `{ currency: "AUD" | "USD", rate, date }`
// (rate = A$ per US$1, from /api/fx). Without a rate, A$ can't be shown
// honestly, so amounts stay in US$ rather than using a guessed rate.

export const SYMBOLS = { AUD: "A$", USD: "US$" };

/** The currency amounts are actually shown in. */
export const displayCurrency = (money) =>
  money?.currency === "AUD" && Number.isFinite(money?.rate) && money.rate > 0 ? "AUD" : "USD";

/** A US$ amount in the display currency. */
export const convertUsd = (usd, money) => (displayCurrency(money) === "AUD" ? usd * money.rate : usd);

/**
 * An amount already in `currency`. `precise` is for generation costs, which
 * are usually fractions of a cent: four places under a cent, three under a
 * dollar, so "A$0.0031" doesn't read as "A$0.00".
 */
export const formatAmount = (amount, currency = "USD", { precise = false } = {}) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  const symbol = SYMBOLS[currency] || "US$";
  if (precise) {
    if (n === 0) return `${symbol}0.00`;
    if (n < 0.01) return `${symbol}${n.toFixed(4)}`;
    if (n < 1) return `${symbol}${n.toFixed(3)}`;
    return `${symbol}${n.toFixed(2)}`;
  }
  if (n > 0 && n < 0.005) return `under ${symbol}0.01`;
  return `${symbol}${n.toFixed(2)}`;
};

/** A US$ amount, shown in the viewer's currency. */
export const formatUsd = (usd, money, options) =>
  formatAmount(convertUsd(Number(usd), money), displayCurrency(money), options);

/** A per-million-token price: cents from a dollar up, up to three places below. */
export const formatPrice = (usd, money) => {
  const value = convertUsd(usd, money);
  const shown = value >= 1 ? value.toFixed(2) : String(Number(value.toFixed(3)));
  return `${SYMBOLS[displayCurrency(money)]}${shown}`;
};

/** "US$1 = A$1.44 (European Central Bank, 8 Oct)", or null when not converting. */
export const describeRate = (money) => {
  if (displayCurrency(money) !== "AUD") return null;
  const day = money.date
    ? new Date(`${money.date}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short" })
    : null;
  return `US$1 = A$${money.rate.toFixed(2)} (European Central Bank${day ? `, ${day}` : ""})`;
};
